/**
 * useProjectLiveUpdates
 *
 * Hält eine WebSocket-Verbindung zu /ws/project/<projectId> offen,
 * solange der Hook gemountet ist und ein Projekt + Auth-Token vorhanden
 * sind. Bei eingehenden Events wird das aktuelle Projekt frisch geladen
 * (selectProject), damit script.js seine Pointclouds entsprechend
 * nachzieht.
 *
 * Reconnect-Strategie: exponentielles Backoff (1s → 2s → 4s → 8s → 30s).
 */

import { useEffect, useRef } from 'react'
import { useAuthStore } from '../store/auth'
import { useProjectPresenceStore } from '../store/presence'
import { useViewerSyncStore } from '../store/viewerSync'
import { API_URL } from '../lib/apiBase'

interface ProjectEvent {
  type: string
  projectId: string
  ts: number
  cloudId?: string
}

function wsUrlFor(projectId: string, token: string): string {
  const base = API_URL || window.location.origin
  const wsBase = base.replace(/^http/i, 'ws')
  // /ws/yjs/-Prefix wegen nginx-Routing — Pfad auf /ws/project/ verkürzbar,
  // sobald nginx einen generischen /ws/-Block hat.
  return `${wsBase}/ws/yjs/project/${encodeURIComponent(projectId)}?token=${encodeURIComponent(token)}`
}

export function useProjectLiveUpdates(): void {
  const projectId = useAuthStore((s) => s.currentProjectId)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    if (!isAuthenticated || !projectId) return

    let ws: WebSocket | null = null
    let reconnectTimer: number | null = null
    let attempt = 0
    let stopped = false

    const reload = async () => {
      try { await useAuthStore.getState().selectProject(projectId) }
      catch (e) { console.warn('[live] reload failed:', e) }
    }

    const connect = () => {
      if (stopped) return
      const token = localStorage.getItem('accessToken')
      if (!token) return
      let url: string
      try { url = wsUrlFor(projectId, token) }
      catch { return }

      try { ws = new WebSocket(url) }
      catch (err) {
        console.warn('[live] WS construct failed:', err)
        scheduleReconnect()
        return
      }

      ws.onopen = () => {
        attempt = 0
        console.log(`[live] connected to project ${projectId}`)
      }
      ws.onmessage = (ev) => {
        let msg: any = null
        try { msg = JSON.parse(String(ev.data)) } catch { return }
        if (!msg || msg.type === 'hello') return
        if (msg.type === 'presence') {
          const users = Array.isArray(msg.users) ? msg.users : []
          useProjectPresenceStore.getState().setUsers(users)
          // States von Usern, die nicht mehr da sind, aufräumen
          const present = new Set(users.map((u: any) => u.id))
          const vs = useViewerSyncStore.getState()
          for (const id of Object.keys(vs.states)) {
            if (!present.has(id)) vs.removeUser(id)
          }
          return
        }
        if (msg.type === 'viewer-state' || msg.type === 'cursor') {
          if (!msg.userId) return
          useViewerSyncStore.getState().updateState(msg.userId, {
            username: msg.username,
            ...(msg.camera && { camera: msg.camera }),
            ...(msg.type === 'cursor' && { cursor: msg.cursor ?? null }),
            ...(msg.type === 'viewer-state' && 'cursor' in msg && { cursor: msg.cursor ?? null }),
          })
          return
        }
        console.log('[live] event:', msg)
        if (msg.type === 'pointcloud.assigned' || msg.type === 'pointcloud.unassigned') {
          reload()
        }
      }
      ws.onerror = (ev) => {
        console.warn('[live] WS error:', ev)
      }
      ws.onclose = (ev) => {
        ws = null
        if (stopped) return
        // 4401/4403/4404 → kein Reconnect, Bedingung wird nicht von alleine besser.
        if ([4401, 4403, 4404].includes(ev.code)) {
          console.warn(`[live] WS closed (${ev.code}): kein Reconnect`)
          return
        }
        scheduleReconnect()
      }
    }

    const scheduleReconnect = () => {
      attempt += 1
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt - 1, 5))
      console.log(`[live] reconnect in ${delay}ms (attempt ${attempt})`)
      reconnectTimer = window.setTimeout(connect, delay)
    }

    connect()

    // Periodisch eigenen Viewer-State + Cursor publishen
    const publishInterval = window.setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      const W = window as any
      const view = W.viewer?.scene?.view
      if (!view) return
      const pos = view.position
      const pivot = view.getPivot ? view.getPivot() : pos
      if (!pos || !pivot) return
      // Cursor aus der Statusbar (von script.js gepflegt)
      const cx = parseFloat(document.getElementById('sb-cur-x')?.textContent || '')
      const cy = parseFloat(document.getElementById('sb-cur-y')?.textContent || '')
      const cz = parseFloat(document.getElementById('sb-cur-z')?.textContent || '')
      const cursor: [number, number, number] | null =
        isFinite(cx) && isFinite(cy) && isFinite(cz) ? [cx, cy, cz] : null
      const camera = {
        position: [pos.x, pos.y, pos.z] as [number, number, number],
        target: [pivot.x, pivot.y, pivot.z] as [number, number, number],
        ...(typeof view.yaw === 'number' && { yaw: view.yaw }),
        ...(typeof view.pitch === 'number' && { pitch: view.pitch }),
        ...(typeof view.radius === 'number' && { radius: view.radius }),
      }
      try {
        ws.send(JSON.stringify({ type: 'viewer-state', camera, cursor }))
      } catch { /* ignore */ }
    }, 100)  // 10 Hz

    return () => {
      stopped = true
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      window.clearInterval(publishInterval)
      // Presence + Sync beim Verlassen leeren
      useProjectPresenceStore.getState().setUsers([])
      useViewerSyncStore.getState().clear()
      if (ws) {
        try { ws.close(1000, 'unmount') } catch { /* ignore */ }
        ws = null
      }
    }
  }, [projectId, isAuthenticated])
}
